import type { TextAnimation } from '@lessonup/pins-shared';
import { JSONContent } from '@tiptap/core';

/** Returns the subset of the content that corresponds to the given text animation step. */
export function getAnimatedTextContent(animation: TextAnimation, step: number, content: JSONContent): JSONContent {
  if (animation.granularity === 'component') {
    return content;
  }

  const steps = tipTapContentByParagraph(content);

  return Array.from(steps)[step] ?? content;
}

/**
 * Generator for the frames of the animation of TipTap content by paragraph.
 *
 * Each element from the generator is a progressively more complete version of the
 * content. E.g. the first element only contains the first child, the second
 * element contains the first two children, etc.
 *
 * The last element always contains the full content that was passed in.
 *
 * This is useful for animating text content in slides.
 */
export function* tipTapContentByParagraph(rootContent: JSONContent): Generator<JSONContent> {
  if (!rootContent.content || rootContent.content.length === 0) {
    yield rootContent;
    return;
  }

  // All child nodes seen so far.
  // We return multiple versions of `content`, each an incrementally longer
  // prefix of the content's children.
  const prefix: JSONContent[] = [];

  for (const child of rootContent.content) {
    switch (child.type) {
      case 'customBulletList':
      case 'customOrderedList':
      case 'customListItem':
        for (const truncatedNode of tipTapContentByParagraph(child)) {
          yield {
            ...rootContent,
            content: [...prefix, truncatedNode],
          };
        }
        prefix.push(child);
        break;
      default:
        prefix.push(child);
        if (!isEmpty(child)) {
          yield { ...rootContent, content: [...prefix] };
        }
        break;
    }
  }
}

/**
 * Checks if a paragraph is empty (no meaningful text content, only whitespace).
 */
function isEmpty(node: JSONContent): boolean {
  return (
    (node.text === undefined || node.text.trim().length === 0) &&
    (node.content === undefined || node.content.every(isEmpty))
  );
}
